import { JazzRoomParticipantId, MediaType } from '@salutejs/jazz-sdk-web';

export type SortType = 'audio' | 'video' | 'displayScreen' | 'dominant';
export type WeightMapType = Map<JazzRoomParticipantId, Set<SortType>>;

const SORT_WEIGHT: Record<SortType, number> = {
    audio: 10,
    displayScreen: 100,
    dominant: 11,
    video: 10,
};

export const updateParticipantWeight = (newWeight) => (weightMap: WeightMapType) => {
    const { participantId, isMuted, mediaType } = newWeight;
    const newWeightMap = new Map(weightMap);
    const weight = newWeightMap.get(participantId) || new Set<SortType>();
    if (isMuted) {
        weight.delete(mediaType);
    } else {
        weight.add(mediaType);
    }
    newWeightMap.set(participantId, weight);
    return newWeightMap;
}

export const removeParticipantWeight = ({ participantId }) => (weightMap: WeightMapType) => {
    const newWeightMap = new Map(weightMap);
    newWeightMap.delete(participantId);
    return newWeightMap;
}

export const updateDominantWeight = (payload) => (weightMap: WeightMapType) => {
    const { participantId, prevParticipantId } = payload;
    const newWeightMap = new Map(weightMap);
    if (prevParticipantId) {
        const prevParticipantWeight = newWeightMap.get(prevParticipantId) || new Set<SortType>();
        prevParticipantWeight.delete('dominant');
        newWeightMap.set(prevParticipantId, prevParticipantWeight);
    }
    const participantWeight = newWeightMap.get(participantId) || new Set<SortType>();
    participantWeight.add('dominant');
    newWeightMap.set(participantId, participantWeight);
    return newWeightMap;
};

export const getInitialWeightMap = ({ room, participants }) => (weightMap: WeightMapType) => {
    let newWeightMap = weightMap;
    participants.forEach(({ id }) => {
        for (const mediaType: MediaType of ['audio', 'video', 'displayScreen']) {
            const source = room.getParticipantSource(id, mediaType);
            if (source && !source.isMuted) {
                newWeightMap = updateParticipantWeight({
                    isMuted: false,
                    mediaType: mediaType,
                    participantId: id,
                })(newWeightMap);
            }
        }
    });
    const participantId = room.dominantParticipantId.get();
    if (participantId) {
        newWeightMap = updateDominantWeight({ participantId })(newWeightMap);
    }
    return newWeightMap;
}

export function sum(value: Set<SortType> | undefined): number {
    if (!value) {
        return 0;
    }
    return [...value].reduce((acc, curr) => acc + SORT_WEIGHT[curr], 0);
}
